import {LogOut, Search, Sigma, UserRound, X} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { CalcMeta } from '@/lib/calculators'
import { CALCULATORS } from '@/lib/calculators'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'

interface Props {
  onJump: (id: string) => void
}

function Header({ onJump }: Props) {
  const { isAuthenticated, user } = useAuth()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const results: CalcMeta[] = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return []
    return CALCULATORS.filter(c =>
      c.title.toLowerCase().includes(term)
      || c.short.toLowerCase().includes(term)
      || c.keywords.some(k => k.toLowerCase().includes(term)),
    )
  }, [q])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Auto-jump on first match (debounced via effect)
  useEffect(() => {
    if (results.length === 1 && q.trim().length >= 3) {
      const id = results[0].id
      const t = setTimeout(() => onJump(id), 250)
      return () => clearTimeout(t)
    }
  }, [results, q, onJump])

  const pick = (id: string, label: string) => {
    setQ(label)
    setOpen(false)
    onJump(id)
  }

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0b0f19]/80 border-b border-[var(--line)]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
        <a href="#top" className="flex items-center gap-2.5 shrink-0">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Sigma className="w-5 h-5 text-[#06281b]" strokeWidth={2.5} />
          </span>
          <span className="font-display text-[17px] md:text-lg font-semibold tracking-tight">
            FunMathCalculator<span className="text-emerald-400">.com</span>
          </span>
        </a>

        <div ref={wrapRef} className="relative flex-1 max-w-2xl ml-auto min-w-0">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={q}
              onChange={e => { setQ(e.target.value); setOpen(true) }}
              onFocus={() => setOpen(true)}
              placeholder="Try: circle, fraction, trapezoid, interest, rounding..."
              className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/[0.03] border border-[var(--line)] text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/50"
            />
            {q && (
              <button
                onClick={() => { setQ(''); setOpen(false) }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center text-slate-400"
                type="button"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {open && (q.trim() ? results.length > 0 : true) && (
            <div className="absolute top-full mt-2 left-0 right-0 glass rounded-xl overflow-hidden shadow-2xl shadow-black/50">
              {(q.trim() ? results : CALCULATORS).map(c => (
                <button
                  key={c.id}
                  onClick={() => pick(c.id, c.short)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition border-b border-[var(--line)] last:border-b-0"
                >
                  <div>
                    <div className="text-sm text-slate-100 font-medium">{c.short}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{c.title}</div>
                  </div>
                  <span className="text-[10px] font-mono-math text-emerald-300/80 px-2 py-0.5 rounded border border-emerald-400/20 bg-emerald-400/[0.04] shrink-0">{c.volume}</span>
                </button>
              ))}
              {q.trim() && results.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-slate-500">No matches. Try "circle" or "interest".</div>
              )}
            </div>
          )}
        </div>

        {isAuthenticated ? (
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <span className="max-w-[140px] truncate text-xs text-slate-400" title={user?.email || ''}>
              {user?.email || user?.userName || 'Student'}
            </span>
            <button
              type="button"
              onClick={() => lumi.auth.signOut()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--line)] text-xs text-slate-300 hover:border-rose-400/40 hover:text-rose-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        ) : (
          <Link
            to="/auth"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 text-xs font-medium text-emerald-200 hover:border-emerald-400/60"
          >
            <UserRound className="w-3.5 h-3.5" /> Sign in
          </Link>
        )}
      </div>
    </header>
  )
}

export default Header
